import type { MaybeType } from "@/shared/types";
import type { Note } from "../../note";

export interface SyncPersonsResponse {
  deletedIds: string[];
  personsDto: GetAllPersonsResponse;
}

export interface DeletePersonResponse extends GetAllPersonsResponse {}

export interface UpdatePersonResponse extends Person {}

export type UpdatePersonAvatarRequest = {
  personId: string;
  fileId: string;
};

export type UpdatePersonAvatarResponse = Person;

export type DeletePersonAvatarRequest = {
  personId: string;
  fileId: string;
};

export type DeletePersonAvatarResponse = Person;

type MakeIdUnrequired<T> = Omit<T, "id"> & { id?: string };

type ChangedUpdatePersonRequestFieldsKeys =
  | "age"
  | "professionalActivities"
  | "phones"
  | "emails"
  | "socialNetworks"
  | "websites"
  | "careers"
  | "addresses"
  | "personConnections"
  | "anniversaries"
  | "educations"
  | "competences"
  | "hobbies";

export type UpdatePersonRequest = Omit<Person, ChangedUpdatePersonRequestFieldsKeys> & {
  professionalActivities: MakeIdUnrequired<ProfessionalActivityDto>[];
  phones: MakeIdUnrequired<PhoneDto>[];
  emails: MakeIdUnrequired<EmailDto>[];
  websites: MakeIdUnrequired<WebsiteDto>[];
  socialNetworks: MakeIdUnrequired<SocialNetworkDto>[];
  addresses: MakeIdUnrequired<AddressDto>[];
  careers: MakeIdUnrequired<CareerDto>[];
  anniversaries: MakeIdUnrequired<AnniversaryDto>[];
  educations: MakeIdUnrequired<EducationDto>[];
  hobbies: MakeIdUnrequired<HobbyDto>[];
  competences: MakeIdUnrequired<CompetenceDto>[];
  personConnections: MakeIdUnrequired<PersonConnectionDto>[];
};

export type GetPersonByIdRequest = {
  id: string;
};

export type GetPersonByIdResponse = Person;

export interface CreatePersonResponse extends Person {}

export interface CreatePersonRequest extends Omit<UpdatePersonRequest, "id"> {}

export interface GetAllPersonsResponse {
  count: number;
  persons: Person[];
  syncDate: string;
}

export type Gender = "MALE" | "FEMALE" | "NOT_DEFINED";

type HasNameDto = {
  name: string;
};

interface PeopleFileDto {
  fileId: string;
  fileName: string;
  type: string;
}

type AddressDto = {
  id: string;
  addressType: HasNameDto;
  addressStreet1: string | null;
  addressStreet2: string | null;
  addressCity: string | null;
  addressCountry: string | null;
  addressPostalCode: string | null;
  personId: string | null;
};

type PhoneDto = {
  id: string;
  personId: string;
  phoneType: MaybeType<HasNameDto>;
  number: string;
};

type AnniversaryDto = {
  personId: string;
  id: string;
  anniversaryType: string;
  createdDate: string;
  anniversary: MaybeType<string>;
  anniversarySDate: AnniversarySDate;
  description: string;
};

type EmailDto = {
  personId: string;
  id: string;
  email: string;
  emailType: MaybeType<HasNameDto>;
};

type WebsiteDto = {
  id: string;
  personId: string;
  websiteType: HasNameDto;
  websiteAddress: string;
};

type SocialNetworkDto = {
  id: string;
  personId: string;
  socialNetworkType: HasNameDto;
  socialNetwork: string;
};

type CareerDto = {
  id: string;
  personId: string;
  company: string;
  about: string;
  industry: HasNameDto;
  jobTitle: string;
  careerStart: string;
  careerEnd: string;
};

type CompetenceDto = {
  id: string;
  competenceType: HasNameDto;
  about: string;
};

type HobbyDto = {
  id: string;
  hobbyType: HasNameDto;
  about: string;
};

type PersonConnectionDto = {
  id: string;
  personId: string;
  connectionPersonId: string;
  personConnectionType: HasNameDto;
  additionalInfo: string;
};

type ProfessionalActivityDto = {
  id: string;
  personId: string;
  activity: string;
  role: string;
  industry: HasNameDto;
};

type EducationDto = {
  id: string;
  personId: string;
  school: string;
  major: string;
  educationStart: string;
  educationEnd: string;
  about: string;
};

export type SDate = {
  day: MaybeType<number>;
  month: MaybeType<number>;
  year: MaybeType<number>;
};

export type BirthdaySDate = SDate;
export type AnniversarySDate = SDate;

export enum PersonGroup {
  FAMILY = "Family",
  FRIENDS = "Friends",
  BUSINESS = "Business",
  OTHER = "Other",
  COLLEAGUES = "Colleagues",
}

export interface Person {
  id: string;
  firstName: MaybeType<string>;
  middleName: MaybeType<string>;
  lastName: MaybeType<string>;
  gender: Gender;
  title: MaybeType<string>;
  group: MaybeType<PersonGroup> | string;
  birthday: MaybeType<string>;
  birthdaySDate?: BirthdaySDate;
  additionalInformation: MaybeType<string>;
  avatarFile: MaybeType<PeopleFileDto>;
  addresses: AddressDto[];
  phones: PhoneDto[];
  anniversaries: AnniversaryDto[];
  emails: EmailDto[];
  websites: WebsiteDto[];
  socialNetworks: SocialNetworkDto[];
  careers: CareerDto[];
  competences: CompetenceDto[];
  hobbies: HobbyDto[];
  personConnections: PersonConnectionDto[];
  notes: Note[];
  professionalActivities: ProfessionalActivityDto[];
  educations: EducationDto[];
  tags: HasNameDto[];
  lastModifiedDate: string;
}
